import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * Overlay de búsqueda de Ironside.
 *
 * Reemplaza a `predictive-search.js` en el modal del header. Es un componente aparte y no
 * una modificación de aquel porque `assets/predictive-search.js` está congelado en
 * `scripts/lib/manifest.sh` (diverge entre México, Chile y Argentina) y un cambio ahí no
 * llegaría a las tres tiendas.
 *
 * Estados:
 *   - vacío: lo que vino renderizado del servidor (sugeridas, tendencias, te puede interesar).
 *   - escribiendo: sección `search-overlay-results`, pedida por la Section Rendering API.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - El input de búsqueda.
 * @property {HTMLElement} results - El contenedor de resultados.
 * @property {HTMLElement} resetButton - El botón de limpiar.
 * @property {HTMLFormElement} [form] - El formulario de búsqueda.
 * @property {HTMLElement[]} [resultsItems] - Los ítems navegables con el teclado.
 * @extends {Component<Refs>}
 */
class SearchOverlayComponent extends Component {
  requiredRefs = ['searchInput', 'results', 'resetButton'];

  #controller = new AbortController();

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {string | null} Markup del estado vacío tal como llegó del servidor. */
  #emptyStateMarkup = null;

  #recommendationsLoaded = false;

  /** @returns {DialogComponent | null} */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    this.#emptyStateMarkup = this.refs.results.innerHTML;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Abre y cierra el overlay con CMD+K.
   * @param {KeyboardEvent} event - El evento de teclado.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    // El foco va al input apenas abre, para poder escribir sin tocar nada.
    requestAnimationFrame(() => this.refs.searchInput.focus());
    this.#loadRecommendations();
  };

  /**
   * Mantiene el foco en el input cuando se hace click en una zona no interactiva.
   * @param {MouseEvent} event - El evento de click.
   */
  handleResultsClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const carouselButton = target.closest('[data-search-carousel]');

    if (carouselButton instanceof HTMLElement) {
      this.#scrollCarousel(carouselButton);
      return;
    }

    const isInteractiveElement =
      target.closest('button') || target.closest('a') || target.closest('input') || target.closest('select');

    if (!isInteractiveElement) {
      this.refs.searchInput.focus();
    }
  };

  /**
   * Desplaza el carrusel de "Te puede interesar" una pantalla.
   * @param {HTMLElement} button - El botón que se presionó.
   */
  #scrollCarousel(button) {
    const list = button
      .closest('[data-search-interest]')
      ?.querySelector('.search-overlay__products--carousel');

    if (!(list instanceof HTMLElement)) return;

    const direction = button.dataset.searchCarousel === 'prev' ? -1 : 1;
    const card = list.querySelector('.search-overlay-card');
    const step = card instanceof HTMLElement ? card.offsetWidth + 16 : list.clientWidth;

    list.scrollBy({
      left: step * direction,
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
    });
  }

  /** @returns {HTMLElement[]} Los ítems navegables con el teclado, en orden de lectura. */
  get #allResultsItems() {
    const items = Array.from(this.refs.results.querySelectorAll('[ref="resultsItems[]"]'));

    return /** @type {HTMLElement[]} */ (items.filter((item) => item instanceof HTMLElement));
  }

  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems.findIndex((item) => item.getAttribute('aria-selected') === 'true');
  }

  set #currentIndex(index) {
    const items = this.#allResultsItems;

    if (!items.length) return;

    let activeItem = null;

    for (const [itemIndex, item] of items.entries()) {
      item.classList.remove('keyboard-focus');

      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }

        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
      block: 'nearest',
    });

    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems[this.#currentIndex];
  }

  /**
   * Navega los resultados con el teclado.
   * @param {KeyboardEvent} event - El evento de teclado.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (event.key === 'Enter') {
      if (this.#currentIndex >= 0) {
        event.preventDefault();
        this.#currentItem?.querySelector('a')?.click();
        return;
      }

      const cleanSearchTerm = this.refs.searchInput.value.trim();

      if (!cleanSearchTerm.length) {
        event.preventDefault();
        return;
      }

      // Sin selección, el submit del formulario lleva a la página de resultados.
      return;
    }

    const items = this.#allResultsItems;

    if (!items.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') return;

    const currentIndex = this.#currentIndex;
    const totalItems = items.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = event.shiftKey
          ? currentIndex > 0
            ? currentIndex - 1
            : totalItems - 1
          : currentIndex < totalItems - 1
          ? currentIndex + 1
          : 0;
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;
    }
  };

  /**
   * Vuelve al estado vacío.
   * @param {boolean} [keepFocus=true] - Si hay que dejar el foco en el input.
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }

    this.#resetSearch();
  }, 100);

  /**
   * Busca mientras se escribe.
   * @param {InputEvent} event - El evento de input.
   */
  search = debounce((event) => {
    // Con Escape y otras teclas que no escriben no hay que buscar.
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();

    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  #resetScrollPosition() {
    requestAnimationFrame(() => {
      this.refs.results.scrollTo(0, 0);
    });
  }

  /**
   * Pide los resultados de la búsqueda predictiva y los pinta.
   * @param {string} searchTerm - El término buscado.
   */
  #getSearchResults(searchTerm) {
    const sectionId = this.dataset.resultsSectionId;

    if (!sectionId) return;

    const cleanSearchTerm = String(searchTerm || '').trim();

    if (!cleanSearchTerm.length) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);

    // Un término con números o separadores suele ser un SKU: ahí solo interesan productos.
    const looksLikeSku = cleanSearchTerm.length >= 3 && (/[0-9]/.test(cleanSearchTerm) || /[-_./]/.test(cleanSearchTerm));

    const productsLimit = Number(this.dataset.productsLimit) || 6;
    const collectionsLimit = Number(this.dataset.collectionsLimit) || 6;

    url.searchParams.set('q', cleanSearchTerm);
    // La Predictive Search API no acepta más de 10 por tipo.
    url.searchParams.set('resources[limit]', String(Math.min(Math.max(productsLimit, collectionsLimit), 10)));
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set(
      'resources[options][fields]',
      'title,product_type,variants.title,vendor,variants.sku,variants.barcode'
    );
    // `query` es lo que devuelve las sugerencias de término del motor de Shopify.
    url.searchParams.set('resources[type]', looksLikeSku ? 'product' : 'product,collection,query');

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup || abortController.signal.aborted) return;

        this.#render(resultsMarkup);
        this.#resetScrollPosition();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;

        throw error;
      });
  }

  /**
   * Pinta markup nuevo en el contenedor de resultados.
   * @param {string} markup - El HTML a pintar.
   */
  #render(markup) {
    const wrapper = document.createElement('div');

    wrapper.innerHTML = markup;

    // `morph` viene con `childrenOnly`, así que el contenedor conserva sus refs y listeners.
    morph(this.refs.results, wrapper);
  }

  #hideResetButton() {
    this.refs.resetButton.hidden = true;
  }

  #showResetButton() {
    this.refs.resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();

    this.#activeFetch?.abort();
    this.#activeFetch = abortController;

    return abortController;
  }

  #resetSearch = () => {
    this.#currentIndex = -1;
    this.refs.searchInput.value = '';
    this.#hideResetButton();
    this.#activeFetch?.abort();

    if (this.#emptyStateMarkup == null) return;

    this.#render(this.#emptyStateMarkup);
    this.#resetScrollPosition();
  };

  /**
   * Reemplaza "Te puede interesar" por las recomendaciones nativas de Shopify.
   *
   * La Product Recommendations API necesita un producto semilla, y en un overlay de
   * búsqueda no hay producto de contexto: se usa el último que vio la persona. Si no vio
   * ninguno, o si la API no devuelve nada, queda la colección de respaldo que ya vino
   * renderizada del servidor.
   */
  async #loadRecommendations() {
    if (this.#recommendationsLoaded) return;

    const container = this.refs.results.querySelector('[data-search-interest]');

    if (!(container instanceof HTMLElement) || container.dataset.source !== 'recommendations') return;

    const seedProductId = RecentlyViewed.getProducts()[0];
    const sectionId = this.dataset.recommendationsSectionId;
    const recommendationsRoute = this.dataset.recommendationsUrl;

    if (!seedProductId || !sectionId || !recommendationsRoute) return;

    this.#recommendationsLoaded = true;

    const url = new URL(recommendationsRoute, location.origin);

    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('product_id', seedProductId);
    // La API de recomendaciones no acepta más de 10.
    url.searchParams.set('limit', String(Math.min(Number(container.dataset.limit) || 8, 10)));
    url.searchParams.set('intent', 'related');

    try {
      const response = await fetch(url.toString());

      if (!response.ok) return;

      const markup = await response.text();
      const recommendations = new DOMParser()
        .parseFromString(markup, 'text/html')
        .querySelector('[data-search-interest]');

      if (!recommendations || !recommendations.querySelector('.search-overlay-card')) return;

      if (!container.isConnected) {
        // Mientras llegaba la respuesta la persona empezó a escribir: se reintenta al reabrir.
        this.#recommendationsLoaded = false;
        return;
      }

      // El estado vacío se restaura desde este markup, así que también hay que actualizarlo.
      container.replaceWith(recommendations);
      this.#emptyStateMarkup = this.refs.results.innerHTML;
    } catch {
      // Sin recomendaciones se queda la colección de respaldo.
    }
  }
}

if (!customElements.get('search-overlay-component')) {
  customElements.define('search-overlay-component', SearchOverlayComponent);
}
